import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { CheckCircle, Phone, Mail, Home, Calendar, User, FileText, Loader2, PhoneCall } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Check if current time is within office hours (Mountain Time)
const isOfficeHours = () => {
  const now = new Date();
  const options = { timeZone: 'America/Denver', hour: 'numeric', minute: 'numeric', hour12: false };
  const mtTime = new Intl.DateTimeFormat('en-US', options).format(now);
  const [hour] = mtTime.split(':').map(Number);
  
  const dayOptions = { timeZone: 'America/Denver', weekday: 'short' };
  const dayOfWeek = new Intl.DateTimeFormat('en-US', dayOptions).format(now);
  
  // Office closed on weekends
  if (dayOfWeek === 'Sat' || dayOfWeek === 'Sun') {
    return false;
  }
  
  // Friday: 9 AM - 4 PM
  if (dayOfWeek === 'Fri') {
    return hour >= 9 && hour < 16;
  }
  
  // Monday-Thursday: 9 AM - 5 PM
  return hour >= 9 && hour < 17;
};

const ThankYou = () => {
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [callingAgent, setCallingAgent] = useState(false);
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [callbackPhone, setCallbackPhone] = useState('');

  useEffect(() => {
    const fetchAgentAssignment = async () => {
      const submissionId = localStorage.getItem('submissionId');
      
      if (!submissionId) {
        setLoading(false);
        return;
      }

      // Poll for agent assignment (Zapier might take a few seconds)
      let attempts = 0;
      const maxAttempts = 10;
      
      const pollInterval = setInterval(async () => {
        attempts++;
        
        try {
          const response = await fetch(`${BACKEND_URL}/api/agent-assignment/${submissionId}`);
          const data = await response.json();
          
          if (data.status === 'success' && data.data) {
            setAgentData(data.data);
            setLoading(false);
            clearInterval(pollInterval);
          } else if (attempts >= maxAttempts) {
            // Stop polling after max attempts
            setLoading(false);
            clearInterval(pollInterval);
          }
        } catch (error) {
          console.error('Error fetching agent assignment:', error);
          if (attempts >= maxAttempts) {
            setError(true);
            setLoading(false);
            clearInterval(pollInterval);
          }
        }
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(pollInterval);
    };

    fetchAgentAssignment();
  }, []);

  const handleScheduleMeeting = () => {
    // Navigate to unified scheduling page
    window.location.href = '/schedule-meeting';
  };

  const handleProvideDetails = () => {
    // Navigate to advanced quote page with submission data
    window.location.href = '/advanced-quote';
  };

  const handleCallAgent = async () => {
    if (!agentData?.agent_phone) {
      toast.error('Agent phone number not available');
      return;
    }
    setShowCallbackModal(true);
  };

  const initiateCallToAgent = async () => {
    if (!callbackPhone) {
      toast.error('Please enter your phone number');
      return;
    }

    setCallingAgent(true);
    try {
      // Format the agent's phone for RingOut
      const agentPhone = agentData.agent_phone.replace(/\D/g, '');
      
      const response = await fetch(`${BACKEND_URL}/api/click-to-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_phone: callbackPhone,
          target_phone: agentPhone.length === 10 ? `+1${agentPhone}` : `+${agentPhone}`,
          submission_id: localStorage.getItem('submissionId')
        })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        toast.success(`Calling you now! You'll be connected to ${agentData.agent_name}`);
        setShowCallbackModal(false);
        setCallbackPhone('');
      } else {
        toast.error(data.message || 'Failed to initiate call');
      }
    } catch (err) {
      console.error('Call error:', err);
      toast.error('Failed to initiate call. Please try again.');
    } finally {
      setCallingAgent(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#003087] via-[#003087] to-[#b4c4ea] flex items-center justify-center px-4 py-8">
      <div className="max-w-3xl w-full bg-white rounded-lg shadow-2xl p-8 md:p-12">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <CheckCircle className="h-20 w-20 text-green-500" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#003087] mb-4">
            Thank You!
          </h1>
          
          <p className="text-xl text-gray-700 mb-8">
            Your quote request has been successfully submitted
          </p>

          {/* Agent Assignment Section */}
          {loading ? (
            <div className="bg-[#b4c4ea]/20 rounded-lg p-6 mb-8">
              <div className="flex items-center justify-center space-x-3">
                <Loader2 className="h-6 w-6 animate-spin text-[#003087]" />
                <p className="text-lg text-gray-700">
                  Assigning your dedicated agent...
                </p>
              </div>
            </div>
          ) : agentData ? (
            <div className="bg-gradient-to-br from-[#003087] to-[#b4c4ea] text-white rounded-lg p-6 mb-8">
              <div className="flex items-center justify-center mb-3">
                <User className="h-6 w-6 mr-2" />
                <p className="text-lg font-semibold">Your Assigned Agent</p>
              </div>
              <p className="text-3xl font-bold mb-2">
                {agentData.agent_name}
              </p>
              {agentData.agent_email && (
                <p className="text-sm opacity-90 flex items-center justify-center">
                  <Mail className="h-4 w-4 mr-2" />
                  {agentData.agent_email}
                </p>
              )}
              {agentData.agent_phone && (
                <div className="flex items-center justify-center mt-2 gap-3">
                  <p className="text-sm opacity-90 flex items-center">
                    <Phone className="h-4 w-4 mr-2" />
                    {agentData.agent_phone}
                  </p>
                  {isOfficeHours() && (
                    <button
                      onClick={handleCallAgent}
                      className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white text-sm px-3 py-1 rounded-full transition-colors"
                      data-testid="call-agent-btn"
                    >
                      <PhoneCall className="h-4 w-4" />
                      Call Now
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : null}

          <div className="space-y-4 mb-8">
            <p className="text-gray-700">
              One of our licensed agents from AP Agency will contact you within 24 hours to discuss your personalized insurance quote.
            </p>
            <p className="text-gray-600">
              In the meantime, you can take the following actions:
            </p>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 max-w-xl mx-auto">
            <button
              onClick={handleScheduleMeeting}
              className="flex flex-col items-center justify-center p-6 bg-white border-2 border-[#003087] rounded-lg hover:bg-[#003087] hover:text-white transition-all duration-300 group"
            >
              <Calendar className="h-8 w-8 text-[#003087] group-hover:text-white mb-3" />
              <span className="font-semibold text-[#003087] group-hover:text-white">Schedule a Meeting</span>
              <span className="text-sm text-gray-600 group-hover:text-white/80 mt-1">Phone, Video, or In-Person</span>
            </button>

            <button
              onClick={handleProvideDetails}
              className="flex flex-col items-center justify-center p-6 bg-white border-2 border-[#003087] rounded-lg hover:bg-[#003087] hover:text-white transition-all duration-300 group"
            >
              <FileText className="h-8 w-8 text-[#003087] group-hover:text-white mb-3" />
              <span className="font-semibold text-[#003087] group-hover:text-white">Provide More Details</span>
              <span className="text-sm text-gray-600 group-hover:text-white/80 mt-1">Help us serve you better</span>
            </button>
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center border-t border-gray-200 pt-8">
            <Button
              onClick={() => window.location.href = '/'}
              className="bg-[#003087] hover:bg-[#003087]/90 text-white px-6 py-3"
            >
              <Home className="h-5 w-5 mr-2" />
              Back to Home
            </Button>
            <Button
              onClick={() => window.location.href = '/contact'}
              variant="outline"
              className="border-2 border-[#003087] text-[#003087] hover:bg-[#003087] hover:text-white px-6 py-3"
            >
              <Phone className="h-5 w-5 mr-2" />
              Contact Us
            </Button>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Need immediate assistance? Call us at <a href="tel:+19703154628" className="text-[#003087] font-semibold hover:underline">(970) 315-4628</a> or use the chat widget.
            </p>
          </div>
        </div>
      </div>

      {/* Call Agent Modal */}
      {showCallbackModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <h3 className="text-xl font-bold text-[#003087] mb-4">
              Call {agentData?.agent_name}
            </h3>
            <p className="text-gray-600 mb-4">
              Enter your phone number and we'll connect you directly to your agent.
            </p>
            <input
              type="tel"
              placeholder="(970) 555-1234"
              value={callbackPhone}
              onChange={(e) => setCallbackPhone(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-[#003087]"
              data-testid="callback-phone-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCallbackModal(false);
                  setCallbackPhone('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={initiateCallToAgent}
                disabled={callingAgent}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="initiate-call-btn"
              >
                {callingAgent ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Calling...
                  </>
                ) : (
                  <>
                    <PhoneCall className="h-4 w-4" />
                    Call Me
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThankYou;
